import { PLUGINS } from 'queries/query-keys';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import PluginService from 'services/PluginService';
import { refreshPluginAppList } from 'store/authentication/authenticationAction';
import { PluginModal } from './PluginModal';
import useModal from '__components/Modals/useModal';
import { ButtonColors } from '@kizen/kds/Colors';
import { OnShowModalFn } from 'ts-components/ScriptRunner/types';
import { StyledLoader } from 'app/styles';
import { INDICATOR_TYPE } from './constants';

interface FloatingFrameConfig {
  api_name: string;
  css?: string;
  default_position?: 'bottom-left' | 'bottom-right';
  event_scripts?: Record<string, string>;
  header_color?: string;
  header_text_color?: string;
  height?: number;
  html?: string;
  name: string;
  script?: string;
  title: string;
  type: 'script' | 'iframe' | 'html';
  width?: number;
  match?: Array<string>;
  ignore?: Array<string>;
  message_handler?: string;
  minimized_style?: string;
  minimized_config?: Record<string, any>;
  base_config?: Record<string, any>;
}

interface RoutablePageConfig {
  api_name: string;
  callback?: string;
  css?: string;
  event_scripts?: Record<string, string>;
  html?: string;
  iframe_url?: string;
  name: string;
  script?: string;
  type: 'script' | 'iframe' | 'html';
  is_toolbar_item?: boolean;
  toolbar_icon?: string;
  toolbar_color?: string;
}

interface ToolbarItemConfig {
  api_name: string;
  color?: string;
  icon?: string;
  label: string;
  script: string;
}

interface RouteScriptConfig {
  api_name: string;
  blocking: boolean;
  name: string;
  routes: Array<string>;
  script: string;
  custom_object: {
    id: string;
    name: string;
    object_name: string;
  };
}

interface DataAdornmentConfig {
  id?: string;
  field_type: 'phonenumber';
  script: string;
  config: {
    icon: string;
    color: string;
    tooltip: string;
  };
}

export type FrameQuadrant =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export type FramePosition = {
  id?: string;
  left: number;
  top: number;
  quadrant?: FrameQuadrant;
  deltas?: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
};

export type EmployeeConfigMap = {
  minimized?: boolean;
  position?: FramePosition;
  hide_floating_frame?: boolean;
};

type EmployeeConfig = Record<string, EmployeeConfigMap>;

interface AdditionalConfigParams {
  api_name?: string;
  script_id?: string;
  plugin_api_name: string;
  plugin_name: string;
  employee_config: EmployeeConfig;
  args: Record<string, any>;
}

export type FloatingFrame = FloatingFrameConfig & AdditionalConfigParams;
export type RoutablePage = RoutablePageConfig & AdditionalConfigParams;
export type DataAdornment = DataAdornmentConfig & AdditionalConfigParams;
export type ExecutionPlugin = DataAdornment & {
  script_id?: string;
  api_name?: string;
};
export type ToolbarItem = ToolbarItemConfig & AdditionalConfigParams;
export type RouteScript = RouteScriptConfig & AdditionalConfigParams;

type FloatingFrames = Array<FloatingFrame>;
type RoutablePages = Array<RoutablePage>;
type DataAdornments = Array<DataAdornment>;
type ToolbarItems = Array<ToolbarItem>;
type RouteScripts = Array<RouteScript>;

interface DefaultConfig {
  floatingFrames: FloatingFrames;
  routablePages: RoutablePages;
  dataAdornments: DataAdornments;
  toolbarItems: ToolbarItems;
  routeScripts: RouteScripts;
}

type PluginConfig = Record<
  string,
  {
    floating_frames: FloatingFrames;
    routable_pages: RoutablePages;
    data_adornments: DataAdornments;
    name: string;
    employee_config: EmployeeConfig;
    business_config: Record<string, any>;
    toolbar_items: ToolbarItems;
    browser_route_scripts: RouteScripts;
  }
>;

type ButtonConfig = {
  label: string;
  variant?: 'text' | 'standard';
  color?: ButtonColors;
};

type ModalDescription = {
  type: 'description';
  content: string;
  widthPercent: 50 | 100;
};

type ModalTextInput = {
  type: 'text';
  label: string;
  placeholder: string;
  widthPercent: 50 | 100;
  id: string;
  defaultValue?: string;
};

type ModalSpacer = {
  type: 'spacer';
  height: number;
  widthPercent: 50 | 100;
};

type ModalDropdown = {
  type: 'dropdown';
  label: string;
  placeholder: string;
  widthPercent: 50 | 100;
  id: string;
  multiselect?: boolean;
  options: Array<{ label: string; value: string }>;
  defaultValue?: string;
};

type ModalBlock =
  | ModalDescription
  | ModalTextInput
  | ModalSpacer
  | ModalDropdown;

export type ModalConfig = {
  title?: string;
  confirmButton?: ButtonConfig;
  cancelButton?: ButtonConfig;
  content?: Array<ModalBlock>;
};

type RouteScriptRunState = Record<
  string,
  {
    waitingOnRouteScript: boolean;
  }
>;

export enum ModalCancelEventSource {
  button = 'button',
  close = 'close',
}

export const PluginContext = createContext<{
  isLoading: boolean;
  pluginNames: string[];
  floatingFrames: DefaultConfig['floatingFrames'];
  routablePages: DefaultConfig['routablePages'];
  dataAdornments: DefaultConfig['dataAdornments'];
  touchFloatingFrame: (
    frame: string,
    cb?: (arg: Array<string>) => void
  ) => void;
  floatingFrameOrder: string[];
  floatingFrameOffset: Record<string, number>;
  refetch: () => void;
  fullRefetch: () => void;
  pluginAPINamesToIds: Record<string, string>;
  hiddenByModal: boolean;
  terminators: MutableRefObject<Record<string, Array<() => void>>>;
  cleanupWorkers: () => void;
  plugins: PluginConfig;
  sessionData: Record<string, Record<string, any>>;
  setSessionData: (pluginId: string, state: Record<string, any>) => void;
  toolbarItems: ToolbarItems;
  toolbarItemsByFullId: Record<string, ToolbarItem>;
  handleShowModal: OnShowModalFn;
  waitingOnRouteScript: boolean;
  routeScripts: RouteScripts;
  routeScriptRunState: RouteScriptRunState;
  setRouteScriptRunState: (
    setter: (val: RouteScriptRunState) => RouteScriptRunState
  ) => void;
  getRunnerState: (key?: string) => {
    indicator: string;
    hidden: boolean;
    pending: boolean;
  };
  getRunnerStateUpdater: (
    key: string
  ) => (
    getNewState: (currentState: Record<string, any>) => Record<string, any>
  ) => void;
}>({
  isLoading: true,
  pluginNames: [],
  floatingFrames: [],
  routablePages: [],
  dataAdornments: [],
  floatingFrameOrder: [],
  touchFloatingFrame: () => {},
  floatingFrameOffset: {},
  refetch: () => {},
  fullRefetch: () => {},
  pluginAPINamesToIds: {},
  hiddenByModal: false,
  terminators: { current: {} },
  cleanupWorkers: () => {},
  plugins: {},
  sessionData: {},
  setSessionData: () => {},
  toolbarItems: [],
  toolbarItemsByFullId: {},
  handleShowModal: () => {},
  waitingOnRouteScript: false,
  routeScripts: [],
  routeScriptRunState: {},
  setRouteScriptRunState: () => {},
  getRunnerStateUpdater:
    (key: string) =>
    (
      getNewState: (currentState: Record<string, any>) => Record<string, any>
    ) => {},
  getRunnerState: () => ({
    indicator: INDICATOR_TYPE.NONE,
    hidden: false,
    pending: false,
  }),
});

const defaultConfig: DefaultConfig = {
  floatingFrames: [],
  routablePages: [],
  dataAdornments: [],
  toolbarItems: [],
  routeScripts: [],
};

function pluginMapper<T>(config: T, apiName: string, plugin: any) {
  return {
    ...config,
    plugin_api_name: apiName,
    plugin_name: plugin.name,
    employee_config: plugin.employee_config,
    args: plugin.business_config,
  };
}

export const PluginContextWrapper = ({ children }: { children: any }) => {
  const [routeScriptRunState, setRouteScriptRunState] =
    useState<RouteScriptRunState>({});

  const waitingOnRouteScript = useMemo(() => {
    return Object.values(routeScriptRunState).some(
      (state) => state.waitingOnRouteScript
    );
  }, [routeScriptRunState]);

  const pluginApps: Array<Record<string, string>> = useSelector(
    (s: any) => s?.authentication?.pluginApps
  );
  const [hiddenByModal, setHiddenByModal] = useState(false);
  const [sessionData, _setSessionData] = useState<
    Record<string, Record<string, any>>
  >({});
  const terminators = useRef<Record<string, Array<() => void>>>({});
  const dispatch = useDispatch<any>();

  const configRef = useRef<any>({});
  const cbRef = useRef<(...args: any) => void>();
  const [modalQueue, setModalQueue] = useState<
    Array<{ config: ModalConfig; cb: (...args: any) => void }>
  >([]);

  const [integrationModalProps, , integrationModalTriggerProps] = useModal({});

  const { onConfirm, show, onHide } = integrationModalProps;
  const { show: showPrompt, showing } = integrationModalTriggerProps;

  const handleShowModal = useCallback((config: ModalConfig, cb: () => void) => {
    setModalQueue((prev) => [...prev, { config, cb }]);
  }, []);

  if (modalQueue.length > 0 && !showing) {
    configRef.current = modalQueue[0].config;
    cbRef.current = modalQueue[0].cb;
    showPrompt();
  }

  const completeModal = useCallback(() => {
    configRef.current = undefined;
    cbRef.current = undefined;
    setModalQueue((prev) => prev.slice(1));
  }, []);

  const derivedModalState = useMemo(() => {
    return {
      props: {
        show,
        onConfirm: (values: Record<string, any>) => {
          onConfirm();
          if (cbRef.current) {
            cbRef.current({ canceled: false, values });
          }
          completeModal();
        },
        onHide: (eventSource: ModalCancelEventSource, ...args: any) => {
          onHide(...args);
          if (cbRef.current) {
            cbRef.current({ canceled: true, values: {}, eventSource });
          }
          completeModal();
        },
      },
      config: configRef.current,
    };
  }, [onConfirm, onHide, show, completeModal]);

  const setSessionData = useCallback(
    (pluginId: string, update: Record<string, any>) => {
      _setSessionData((prev) => ({
        ...prev,
        [pluginId]: {
          ...prev[pluginId],
          ...update,
        },
      }));
    },
    []
  );

  const cleanupWorkers = useCallback(() => {
    Object.keys(terminators.current).forEach((key) => {
      terminators.current[key]?.forEach((fn) => fn());
      terminators.current[key] = [];
    });
  }, []);

  const pluginAPINamesToIds = useMemo(() => {
    return (
      pluginApps?.reduce?.((acc, plugin) => {
        return {
          ...acc,
          [plugin.api_name]: plugin.id,
        };
      }, {}) ?? {}
    );
  }, [pluginApps]);

  const [floatingFrameOrder, setFloatingFrameOrder] = useState<Array<string>>(
    []
  );

  const {
    data = {},
    isLoading,
    refetch,
  } = useQuery<PluginConfig>({
    queryKey: PLUGINS.LIST,
    queryFn: async () => {
      // we smoother the error
      try {
        return PluginService.get();
      } catch (error) {}
    },
    enabled: pluginApps?.length > 0,
    throwOnError: false,
  });

  const pluginNames = useMemo(() => {
    return Object.keys(data ?? {});
  }, [data]);

  const [runnerState, setRunnerState] = useState<
    Record<
      string,
      { indicator: INDICATOR_TYPE; hidden: boolean; pending: boolean }
    >
  >({});

  const getRunnerStateUpdater = useCallback(
    (key: string) =>
      (
        getNewState: (currentState: Record<string, any>) => Record<string, any>
      ) => {
        setRunnerState((prev: Record<string, any>) => {
          const result = {
            ...prev,
            [key]: getNewState(prev[key]),
          };
          return result;
        });
      },
    []
  );

  const getRunnerState = useCallback(
    (key?: string) => {
      if (!key || !runnerState[key]) {
        return {
          indicator: INDICATOR_TYPE.NONE,
          hidden: false,
          pending: false,
        };
      }
      return {
        ...runnerState[key],
      };
    },
    [runnerState]
  );

  const {
    floatingFrames,
    routablePages,
    dataAdornments,
    toolbarItems: unsortedToolbarItems,
    routeScripts,
  } = useMemo(() => {
    return pluginNames.reduce((acc, pluginApiName) => {
      const plugin = data[pluginApiName];

      return {
        ...acc,
        floatingFrames: [
          ...acc.floatingFrames,
          ...(plugin.floating_frames?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        routablePages: [
          ...acc.routablePages,
          ...(plugin.routable_pages?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        dataAdornments: [
          ...acc.dataAdornments,
          ...(plugin.data_adornments?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        toolbarItems: [
          ...acc.toolbarItems,
          ...(plugin.toolbar_items?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
          ...(plugin.routable_pages
            ?.filter((c) => c.is_toolbar_item)
            ?.map((config) => {
              return {
                ...pluginMapper(config, pluginApiName, plugin),
                icon: config.toolbar_icon,
                label: config.name,
                color: config.toolbar_color,
                script: '',
              };
            }) ?? []),
        ],
        routeScripts: [
          ...acc.routeScripts,
          ...(plugin.browser_route_scripts?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
      };
    }, defaultConfig);
  }, [data, pluginNames]);

  const toolbarItems = useMemo(() => {
    return unsortedToolbarItems.sort((a, b) => a.label.localeCompare(b.label));
  }, [unsortedToolbarItems]);

  const toolbarItemsByFullId = useMemo(() => {
    return toolbarItems.reduce((acc, item) => {
      return {
        ...acc,
        [`${item.plugin_api_name}-${item.api_name}`]: item,
      };
    }, {});
  }, [toolbarItems]);

  const touchFloatingFrame = useCallback(
    (id: string, cb?: (value: Array<string>) => void) => {
      setFloatingFrameOrder((prev) => {
        const res = prev.filter((i) => i !== id);
        const done = [id, ...res];
        cb?.(done);
        return done;
      });
    },
    []
  );

  const floatingFrameOffset = useMemo(() => {
    return [...floatingFrameOrder].reverse().reduce((acc, id, index) => {
      return {
        ...acc,
        [id]: index,
      };
    }, {});
  }, [floatingFrameOrder]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const modal = document.querySelector('div[role="dialog"]');
      if (modal) {
        setHiddenByModal(true);
      } else {
        setHiddenByModal(false);
      }
    });

    observer.observe(document.body, {
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      cleanupWorkers();
    };
  }, [cleanupWorkers]);

  const fullRefetch = useCallback(async () => {
    const res = await refetch();
    await dispatch(refreshPluginAppList());

    return res;
  }, [refetch, dispatch]);

  const showLoadingIndicator = useMemo(() => {
    const keys = Object.keys(runnerState);
    return (
      keys.length > 0 &&
      keys.some((name) => runnerState[name]?.indicator === INDICATOR_TYPE.BLOCK)
    );
  }, [runnerState]);

  return (
    <PluginContext.Provider
      value={{
        isLoading,
        pluginNames,
        floatingFrames: isLoading ? [] : floatingFrames,
        routablePages,
        dataAdornments,
        floatingFrameOrder,
        floatingFrameOffset,
        touchFloatingFrame,
        refetch,
        fullRefetch,
        pluginAPINamesToIds,
        hiddenByModal,
        terminators,
        cleanupWorkers,
        plugins: data,
        sessionData,
        setSessionData,
        toolbarItems,
        toolbarItemsByFullId,
        handleShowModal,
        waitingOnRouteScript,
        routeScripts,
        routeScriptRunState,
        setRouteScriptRunState,
        getRunnerStateUpdater,
        getRunnerState,
      }}
    >
      {showLoadingIndicator ? <StyledLoader loading /> : null}
      {derivedModalState.props.show ? (
        <PluginModal modalState={derivedModalState} />
      ) : null}
      {children}
    </PluginContext.Provider>
  );
};
